import { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthInit } from './hooks/use-auth';
import { AuthGuard } from './components/auth-guard';
import { LoginPage } from './routes/login';
import { LazyRoutes } from './routes/lazy-routes';
import { RoutePrefetcher } from './components/route-prefetcher';
import { useIsAdmin, useIsAssignedTasksOnly, usePermission } from './hooks/use-permission';
import { getDefaultAuthenticatedPath } from './lib/page-access';
import { useThemeStore } from './stores/theme.store';
import { NavigationFeedback } from './components/navigation-feedback';

function DefaultRedirect() {
  const isAdmin = useIsAdmin();
  const assignedTasksOnly = useIsAssignedTasksOnly();
  const canViewDashboard = usePermission('view_dashboard');

  return (
    <Navigate
      to={getDefaultAuthenticatedPath({
        isAdmin,
        assignedTasksOnly,
        canViewDashboard,
        canViewTeam: false,
        canViewReports: false,
      })}
      replace
    />
  );
}

function DashboardRoute() {
  return (
    <AuthGuard requiredPermission="view_dashboard" blockAssignedTasksOnly>
      <LazyRoutes.Dashboard />
    </AuthGuard>
  );
}

/** Fallback for the public (unauthenticated) routes, which render no chrome of
 *  their own. Authenticated routes suspend *inside* AuthGuard instead, so the
 *  sidebar stays painted while a route chunk loads. */
function PublicRouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function App() {
  // Initialize Supabase auth state and subscribe to changes
  useAuthInit();

  // The inline script in index.html already stamped the theme before first
  // paint; this re-syncs the store and starts following OS changes for the
  // life of the tab.
  const initTheme = useThemeStore((state) => state.init);
  useEffect(() => initTheme(), [initTheme]);

  return (
    <>
      <NavigationFeedback />
      <RoutePrefetcher />
      <Suspense fallback={<PublicRouteFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/onboarding" element={<LazyRoutes.Onboarding />} />
          <Route path="/reset-password" element={<LazyRoutes.ResetPassword />} />
          <Route path="/invite/:token" element={<LazyRoutes.Invite />} />

          {/* Protected routes */}
          <Route
            path="/home"
            element={
              <AuthGuard>
                <LazyRoutes.Home />
              </AuthGuard>
            }
          />
          <Route path="/dashboard" element={<DashboardRoute />} />
          <Route
            path="/my-tasks"
            element={
              <AuthGuard>
                <LazyRoutes.MyTasks />
              </AuthGuard>
            }
          />
          <Route
            path="/users"
            element={
              <AuthGuard>
                <LazyRoutes.Users />
              </AuthGuard>
            }
          />
          <Route
            path="/users/invite"
            element={
              <AuthGuard>
                <LazyRoutes.UserInvite />
              </AuthGuard>
            }
          />
          <Route
            path="/users/:id"
            element={
              <AuthGuard>
                <LazyRoutes.UserDetail />
              </AuthGuard>
            }
          />

          {/* Roles management (admin only) */}
          <Route
            path="/roles"
            element={
              <AuthGuard>
                <LazyRoutes.Roles />
              </AuthGuard>
            }
          />
          <Route
            path="/roles/new"
            element={
              <AuthGuard>
                <LazyRoutes.RoleNew />
              </AuthGuard>
            }
          />
          <Route
            path="/roles/:id"
            element={
              <AuthGuard>
                <LazyRoutes.RoleDetail />
              </AuthGuard>
            }
          />

          {/* Form templates (the custom form builder) */}
          <Route
            path="/form-templates"
            element={
              <AuthGuard requiredPermission="manage_form_templates">
                <LazyRoutes.FormTemplates />
              </AuthGuard>
            }
          />
          <Route
            path="/form-templates/:id"
            element={
              // fullWidth: the builder is a three-pane workspace, and the
              // default max-w-7xl wrapper would squeeze the A4 canvas.
              <AuthGuard requiredPermission="manage_form_templates" fullWidth>
                <LazyRoutes.FormTemplateBuilder />
              </AuthGuard>
            }
          />

          {/* Notifications */}
          <Route
            path="/notifications"
            element={
              <AuthGuard>
                <LazyRoutes.Notifications />
              </AuthGuard>
            }
          />

          <Route
            path="/settings"
            element={
              <AuthGuard>
                <LazyRoutes.Settings />
              </AuthGuard>
            }
          />

          {/* Projects */}
          <Route
            path="/projects"
            element={
              <AuthGuard>
                <LazyRoutes.Projects />
              </AuthGuard>
            }
          />
          <Route
            path="/projects/new"
            element={
              <AuthGuard>
                <LazyRoutes.ProjectNew />
              </AuthGuard>
            }
          />
          <Route
            path="/projects/:id/forms/:formId"
            element={
              <AuthGuard requiredPermission="access_forms">
                <LazyRoutes.ProjectForm />
              </AuthGuard>
            }
          />
          <Route
            path="/projects/:id/tasks/new"
            element={
              <AuthGuard>
                <LazyRoutes.TaskNew />
              </AuthGuard>
            }
          />
          <Route
            path="/projects/:id/tasks/:taskId"
            element={
              <AuthGuard>
                <LazyRoutes.TaskDetail />
              </AuthGuard>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <AuthGuard>
                <LazyRoutes.ProjectDetail />
              </AuthGuard>
            }
          />

          {/* Team (merged Resources + Performance) */}
          <Route
            path="/team"
            element={
              <AuthGuard requiredPermission="view_resource_load" blockAssignedTasksOnly>
                <LazyRoutes.Team />
              </AuthGuard>
            }
          />

          {/* Redirects from old routes */}
          <Route path="/resources" element={<Navigate to="/team" replace />} />
          <Route path="/performance" element={<Navigate to="/team" replace />} />

          {/* Reports */}
          <Route
            path="/reports"
            element={
              <AuthGuard requiredPermission="view_reports" fullWidth blockAssignedTasksOnly>
                <LazyRoutes.Reports />
              </AuthGuard>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<DefaultRedirect />} />
          <Route path="*" element={<LazyRoutes.NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
